import { useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import Header from "../../../../../components/common/header";
import LoadingSpinnerFull from "../../../../../components/common/loading-spinner/loading-spinner-full";
import MonthNavigator, { isSettlementMonthOpen } from "../components/month-navigator";
import SettlementOrderTable, {
  type SettlementOrder,
} from "../components/settlement-order-table";

// ─── Query ───────────────────────────────────────────────────────────────────

const GET_PAID_ORDERS = gql`
  query GetSettlementPaidOrders(
    $type: String!
    $status: String!
    $startDate: String!
    $endDate: String!
    $settled: Boolean
  ) {
    getStoreOrders(
      type: $type
      status: $status
      startDate: $startDate
      endDate: $endDate
      settled: $settled
    ) {
      id
      sellerAmount
      sellerAmountWithFee
      createdAt
      packId
      totalQuantity
      pack {
        id
      }
      products {
        id
      }
      payout {
        id
        paidAt
        tossPayoutStatus
      }
      settle {
        paidAt
        state
      }
    }
  }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getMonthRange(date: Date) {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return {
    startDate: toLocalDateStr(start),
    endDate: toLocalDateStr(end),
  };
}

function toLocalDateStr(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function SettlementPaid() {
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const { startDate, endDate } = getMonthRange(currentMonth);

  const { data, loading } = useQuery(GET_PAID_ORDERS, {
    variables: {
      type: "ALL",
      status: "CONFIRMED",
      startDate: new Date(startDate + "T00:00:00").toISOString(),
      endDate: new Date(endDate + "T23:59:59").toISOString(),
      settled: true,
    },
    fetchPolicy: "cache-first",
  });

  const orders: SettlementOrder[] = useMemo(() => {
    if (!data?.getStoreOrders) return [];
    return [...(data.getStoreOrders as SettlementOrder[])]
      .filter((o): o is SettlementOrder => o != null)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
  }, [data]);

  const totalSellerAmount = useMemo(
    () => orders.reduce((sum, o) => sum + (o.sellerAmount || 0), 0),
    [orders],
  );
  const totalSettlement = useMemo(
    () => orders.reduce((sum, o) => sum + (o.sellerAmountWithFee || 0), 0),
    [orders],
  );

  const goToPrevMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth((prev) => {
      const next = new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
      return isSettlementMonthOpen(next) ? next : prev;
    });
  };

  return (
    <div className="w-full min-h-screen flex flex-col bg-gray-50">
      <Header title="정산 완료 내역" showBackButton />

      {/* 월 네비게이션 */}
      <MonthNavigator
        currentMonth={currentMonth}
        onPrev={goToPrevMonth}
        onNext={goToNextMonth}
      />

      {/* 요약 카드 */}
      <div className="mx-4 mt-2 bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex justify-between items-center mb-3">
          <span className="text-gray-600">정산 완료 매출액</span>
          <span className="text-xl font-bold text-gray-900">
            {loading ? "..." : `${totalSellerAmount.toLocaleString()}원`}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600">정산 완료 실수령액</span>
          <span className="text-xl font-bold text-green-600">
            {loading ? "..." : `${totalSettlement.toLocaleString()}원`}
          </span>
        </div>
      </div>

      {/* 정산 완료 내역 표 */}
      {loading ? (
        <LoadingSpinnerFull />
      ) : orders.length > 0 ? (
        <SettlementOrderTable orders={orders} showPaidAt />
      ) : (
        <div className="flex-1 flex items-center justify-center py-16">
          <p className="text-gray-400 text-lg">해당 월의 정산 완료 내역이 없습니다.</p>
        </div>
      )}
    </div>
  );
}
